<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
    <div
      class="relative w-full max-w-md mx-4 rounded-2xl overflow-hidden"
      style="background: #0D0F14; border: 1px solid #1E2733;"
    >
      <!-- Mode artwork -->
      <div class="relative h-32 overflow-hidden">
        <img
          v-if="modeArt && !modeArtFailed"
          :src="modeArt"
          alt=""
          data-test="mode-art"
          class="w-full h-full object-cover opacity-60"
          @error="modeArtFailed = true"
        >
        <div class="absolute inset-0" style="background: linear-gradient(to bottom, rgba(13, 15, 20, 0.1), #0D0F14);" />
        <span
          v-if="lobby.play_mode?.friendly_name"
          data-test="mode-name"
          class="absolute top-3 left-3 flex items-baseline gap-2 rounded-md px-2.5 py-1 text-sm font-semibold text-white backdrop-blur-sm"
          style="background: rgba(13, 15, 20, 0.6);"
        >
          {{ lobby.play_mode.friendly_name }}
          <span v-if="teamFormat" class="text-xs font-medium text-gray-300">{{ teamFormat }}</span>
        </span>
      </div>

      <div class="relative px-8 pb-8 -mt-12 flex flex-col items-center text-center">
        <img
          v-if="lobby.cs_map && !mapPinFailed"
          :src="`/maps/${lobby.cs_map.name}_pin.png`"
          :alt="$t('play.matchmaking.map_label', { map: lobby.cs_map.friendly_name })"
          :title="lobby.cs_map.friendly_name"
          data-test="map-pin"
          class="w-20 h-20 object-contain mb-3"
          style="filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.6));"
          @error="mapPinFailed = true"
        >
        <div v-else class="h-12" />
        <h2 class="text-2xl font-bold text-white mb-6">{{ $t('play.matchmaking.match_found') }}</h2>

        <!-- Players -->
        <div class="flex flex-wrap justify-center gap-4 mb-6">
          <div
            v-for="member in members"
            :key="member.user?.uuid"
            class="flex flex-col items-center gap-2"
          >
            <div class="relative">
              <div
                class="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold overflow-hidden transition-all duration-300"
                :class="member.accepted
                  ? 'bg-primary-600 text-white ring-2 ring-success-400'
                  : 'text-gray-400'"
                :style="member.accepted ? null : 'background: #1A2230;'"
              >
                <img
                  v-if="avatarOf(member)"
                  :src="avatarOf(member)"
                  alt=""
                  data-test="member-avatar"
                  class="w-full h-full object-cover transition-all duration-300"
                  :class="member.accepted ? null : 'opacity-40 grayscale'"
                >
              </div>
              <span
                v-if="member.accepted"
                data-test="accepted-tick"
                class="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-success-400 flex items-center justify-center"
                style="box-shadow: 0 0 0 2px #0D0F14;"
                aria-hidden="true"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 text-gray-950" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-5.121-5.121a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </span>
            </div>
            <span class="text-xs truncate max-w-16" :class="member.accepted ? 'text-white' : 'text-gray-500'">
              {{ memberName(member) }}
            </span>
          </div>
        </div>

        <!-- Ready bar -->
        <div class="w-full mb-6">
          <div class="flex gap-1 mb-2" aria-hidden="true">
            <span
              v-for="slot in members.length"
              :key="slot"
              data-test="ready-segment"
              class="h-1.5 flex-1 rounded-full transition-colors duration-300"
              :class="slot <= acceptedCount ? 'bg-success-400' : 'bg-gray-800'"
            />
          </div>
          <div class="flex items-center justify-end text-sm">
            <span class="sr-only">
              {{ $t('play.matchmaking.players_accepted', { accepted: acceptedCount, total: members.length }) }}
            </span>
            <span class="font-mono" :class="[urgentTimer ? 'text-red-400' : 'text-white', { invisible: allIn }]">
              {{ $t('play.matchmaking.time_remaining', { time: remainingLabel }) }}
            </span>
          </div>
        </div>

        <!-- Accept button -->
        <button
          v-if="!accepted"
          @click="accept"
          :disabled="accepting"
          class="w-full py-4 rounded-xl border border-transparent bg-primary-600 text-lg font-semibold text-white flex items-center justify-center gap-2 shadow-[0_0_32px_rgba(78,84,200,0.35)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          :class="accepting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary-500'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-5.121-5.121a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
          {{ accepting ? $t('play.matchmaking.accepting') : $t('play.matchmaking.accept') }}
        </button>
        <div
          v-else
          :data-test="allIn ? 'everyone-in' : null"
          class="w-full py-4 rounded-xl text-lg font-semibold flex items-center justify-center gap-2 border transition-colors duration-300"
          :class="allIn
            ? 'bg-success-400 border-success-400 text-gray-950'
            : 'bg-success-950 border-success-800 text-white'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" :class="allIn ? 'text-gray-950' : 'text-success-400'" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-5.121-5.121a1 1 0 011.414-1.414L8.414 12.172l6.879-6.879a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
          {{ allIn ? $t('play.matchmaking.everyone_in') : $t('play.matchmaking.accepted_waiting') }}
        </div>

        <div
          v-if="lanManager"
          class="w-full mt-4 pt-4 border-t border-gray-800 flex items-center justify-between gap-3"
        >
          <p class="text-xs text-text-secondary text-left">{{ $t('play.lan_party.cancel_match_hint') }}</p>
          <button
            type="button"
            :disabled="cancelling"
            @click="cancelMatch"
            class="flex-shrink-0 px-3 py-2 rounded-lg text-xs font-semibold text-red-300 hover:bg-red-950 disabled:opacity-50"
            style="background:#2a1215"
          >{{ $t('play.lan_party.cancel_match') }}</button>
        </div>
        <p v-if="error" class="text-red-400 text-xs mt-3">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import useLobbyFound from '../../composables/useLobbyFound';

const props = defineProps({
  lobby: { type: Object, required: true },
});

const {
  accepting, error, members, teamFormat, modeArt, modeArtFailed, mapPinFailed, acceptedCount,
  allIn, accepted, remainingLabel, urgentTimer, memberName, avatarOf, accept, cancelling,
  lanManager, cancelMatch,
} = useLobbyFound(props);
</script>
